const MAX_PREVIEW_LEN = 80;

// Keys that tend to identify an item to a human, in priority order. When an
// item's input is an object we surface one of these values (e.g. the domain or
// name) instead of the generic first-key placeholder.
const PREFERRED_KEYS = [
  'name',
  'title',
  'label',
  'domain',
  'email',
  'url',
  'company',
  'handle',
  'username',
  'slug',
  'id',
  'key',
  'query',
];

function scalarPreview(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed.slice(0, MAX_PREVIEW_LEN);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

/**
 * Produce a short, human-meaningful preview for a for-each item value.
 *
 * Prefers surfacing an actual identifying value (a `name`/`domain`/`email`-style
 * field, otherwise the first scalar) over a generic key name, so run views can
 * label items by *what they are* rather than "Item N". Returns `null` when there
 * is nothing meaningful to show.
 */
export function previewValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const scalar = scalarPreview(value);
  if (scalar !== null) {
    return scalar;
  }
  if (Array.isArray(value)) {
    return `[ ${value.length} ]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length === 0) {
      return '{ }';
    }
    // Prefer a semantically identifying field's value.
    for (const preferred of PREFERRED_KEYS) {
      const match = keys.find((k) => k.toLowerCase() === preferred);
      if (match !== undefined) {
        const preview = scalarPreview(record[match]);
        if (preview !== null) {
          return preview;
        }
      }
    }
    // Otherwise surface the first scalar value present on the object.
    for (const k of keys) {
      const preview = scalarPreview(record[k]);
      if (preview !== null) {
        return preview;
      }
    }
    // Nothing scalar to show — fall back to the first key name.
    const first = keys[0];
    return first !== undefined ? `${first}…` : '{ }';
  }
  return null;
}
